'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { AlertCircle, CheckCircle, AlertTriangle, Info, X, Loader2, Lock, Unlock, Target, Zap, Compass, Smartphone, Mail, Gift, ArrowLeft, ListChecks } from 'lucide-react';
import { SeverityMark } from '@/components/audit/SeverityMark';
import Link from 'next/link';
import { Button } from '@/components/ui/Button';
import { SoftGateModal } from './SoftGateModal';
import { CriterionTag } from './CriterionTag';
import { GUARANTEE } from '@/lib/copy/guarantee';
import { PREVIEW_FINDINGS_COUNT, unlockedCount } from '@/lib/audit/free-gating';
import { parseConversionValue } from '@/lib/audit/break-even';
import styles from './FreeReportResults.module.css';

// Gating numbers live in one place so the report page and the nurture sequence
// cannot drift apart; see src/lib/audit/free-gating.ts

// Severity impact descriptions (business-oriented for conversions)
const severityImpact: Record<string, { en: string; es: string }> = {
  critical: {
    en: '⚠️ Blocking 30%+ of users from converting — fix immediately',
    es: '⚠️ Bloqueando 30%+ de usuarios de convertir — corregir ya',
  },
  serious: {
    en: '📉 Costing you ~15-30% of potential conversions',
    es: '📉 Costándote ~15-30% de conversiones potenciales',
  },
  high: {
    en: '📉 Costing you ~15-30% of potential conversions',
    es: '📉 Costándote ~15-30% de conversiones potenciales',
  },
  moderate: {
    en: '⚡ Causing 5-15% of visitors to leave early',
    es: '⚡ Causando que 5-15% de visitantes abandonen',
  },
  medium: {
    en: '⚡ Causing 5-15% of visitors to leave early',
    es: '⚡ Causando que 5-15% de visitantes abandonen',
  },
  minor: {
    en: '💡 May affect user trust and repeat visits',
    es: '💡 Puede afectar confianza y visitas recurrentes',
  },
  low: {
    en: '💡 May affect user trust and repeat visits',
    es: '💡 Puede afectar confianza y visitas recurrentes',
  },
};

// i18n content
const i18n = {
  en: {
    title: 'Your Free UX Audit Results',
    notAssessedTitle: 'Not assessed',
    notAssessedIntro: 'These checks could not run, so their result is unknown, not clean.',
    accessibilityNotAssessed: 'The accessibility check could not run on this page, so no violation count is shown.',
    analyzedOn: 'Analyzed on',
    accessibilityOverview: 'Accessibility Overview',
    wcagCompliance: 'WCAG compliance check by interfaceaudit.com',
    critical: 'Critical',
    serious: 'Serious',
    moderate: 'Moderate',
    minor: 'Minor',
    allIssuesDetected: 'All Issues Detected',
    showingIssues: (shown: number, total: number) => `Showing ${shown} of ${total} issues found`,
    totalFindings: (count: number) => `${count} total findings`,
    issueDetected: 'Issue detected',
    unlockMoreFree: (count: number) => `Unlock ${count} More Findings - Free`,
    unlockDescription: 'Enter your email to reveal additional UX issues + get our 6-part UX Laws series.',
    unlockWithEmail: 'Unlock with Email',
    unlockExpertAnalysis: 'Unlock Expert UX Analysis',
    professionalAnalysis: 'Professional UX analysis powered by 167 research-backed Audit Criteria',
    completeAnalysis: (count: number | string) => `Complete analysis of all ${count} issues`,
    evaluatedAgainst: 'Evaluated against 167 Audit Criteria',
    visualAttention: 'Visual attention flow & hierarchy analysis',
    detailedRecommendations: 'Detailed recommendations for each issue',
    pdfReport: 'Professional PDF report & full data export',
    getFullReport: 'Get Full UX Report - $99',
    oneTimePayment: GUARANTEE.en.terms,
    wantMoreIssues: 'Want to see more issues? Upgrade to unlock:',
    visualHierarchy: 'Visual Hierarchy Issues',
    performanceBottlenecks: 'Performance Bottlenecks',
    navigationProblems: 'Navigation Problems',
    mobileUxIssues: 'Mobile UX Issues',
    lockedInFree: 'Locked in free tier',
    poweredBy: 'Powered by',
    researchPrinciples: '167 research-backed Audit Criteria',
    getExpertAnalysis: 'Get Expert UX Analysis',
    unlockFullAnalysis: (count: number) => `Unlock full analysis with detailed recommendations for all ${count} issues`,
    oneTimePaymentShort: 'one-time payment',
    completeAnalysisShort: 'Complete analysis of all detected issues',
    continueToCheckout: 'Continue to Checkout',
    processing: 'Processing...',
    moneyBackGuarantee: GUARANTEE.en.checkout,
    emailAddress: 'Email address',
    // B-05 checkout inputs
    successActionLabel: 'What does a successful visit to this page look like?',
    successActionChoose: 'Choose one',
    successActionPurchase: 'Buys something',
    successActionContact: 'Contacts us, requests a quote, or books',
    successActionSignup: 'Signs up or starts a trial',
    successActionInform: 'Finds information or completes a task',
    valueLabelPurchase: 'What is one new order worth? (USD)',
    valueLabelContact: 'What is one new client worth? (USD)',
    valueLabelSignup: 'What is one new signup worth? (USD)',
    valueHint: 'Your number. The report states what one more is worth; it does not predict anything.',
    valueInvalid: 'Enter a number, for example 5000.',
    valueConfirm: '{value} USD each is unusual. I confirm that is right.',
    refinementToggle: 'I have traffic numbers for this page (optional)',
    refinementHint: 'Both are needed for the extra scenarios. Leave blank and the report still works.',
    sessionsLabel: 'Monthly sessions to this page',
    rateLabel: 'Share of visits that complete the action today (%)',
    // New: Urgent issue count
    weFoundIssues: (count: number) => `We found ${count} issues on your site`,
    issuesNeedAttention: 'These issues may be affecting your conversions and user experience',
    // New: Quick Wins teaser
    quickWinsTitle: 'Quick Wins Available',
    quickWinsDescription: 'Easy fixes that take under 1 hour',
    quickWinsLocked: 'Unlock with Pro Report',
    quickWinsSample: 'Preview of actionable fixes:',
    // New: Action Plan teaser
    actionPlanTitle: 'Priority Action Plan',
    actionPlanDescription: 'Top 5 issues to fix first for maximum impact',
    actionPlanLocked: 'Included in Pro Report',
  },
  es: {
    title: 'Resultados de tu Auditoría UX Gratuita',
    notAssessedTitle: 'Sin evaluar',
    notAssessedIntro: 'Estas comprobaciones no pudieron ejecutarse, así que su resultado es desconocido, no limpio.',
    accessibilityNotAssessed: 'La comprobación de accesibilidad no pudo ejecutarse en esta página, así que no se muestra ningún recuento de violaciones.',
    analyzedOn: 'Analizado el',
    accessibilityOverview: 'Resumen de Accesibilidad',
    wcagCompliance: 'Verificación de cumplimiento WCAG por interfaceaudit.com',
    critical: 'Crítico',
    serious: 'Serio',
    moderate: 'Moderado',
    minor: 'Menor',
    allIssuesDetected: 'Todos los Problemas Detectados',
    showingIssues: (shown: number, total: number) => `Mostrando ${shown} de ${total} problemas encontrados`,
    totalFindings: (count: number) => `${count} hallazgos totales`,
    issueDetected: 'Problema detectado',
    unlockMoreFree: (count: number) => `Desbloquea ${count} Hallazgos Más - Gratis`,
    unlockDescription: 'Ingresa tu email para revelar problemas UX adicionales + obtener nuestra serie de 6 Leyes UX.',
    unlockWithEmail: 'Desbloquear con Email',
    unlockExpertAnalysis: 'Desbloquea el Análisis UX Experto',
    professionalAnalysis: 'Análisis UX profesional basado en 167 Criterios de Auditoría respaldados por investigación',
    completeAnalysis: (count: number | string) => `Análisis completo de los ${count} problemas`,
    evaluatedAgainst: 'Evaluado contra 167 Criterios de Auditoría/UI',
    visualAttention: 'Análisis de flujo de atención visual y jerarquía',
    detailedRecommendations: 'Recomendaciones detalladas para cada problema',
    pdfReport: 'Informe PDF profesional y exportación completa de datos',
    getFullReport: 'Obtener Informe Completo - $99',
    oneTimePayment: GUARANTEE.es.terms,
    wantMoreIssues: '¿Quieres ver más problemas? Actualiza para desbloquear:',
    visualHierarchy: 'Problemas de Jerarquía Visual',
    performanceBottlenecks: 'Cuellos de Botella de Rendimiento',
    navigationProblems: 'Problemas de Navegación',
    mobileUxIssues: 'Problemas UX Móvil',
    lockedInFree: 'Bloqueado en versión gratuita',
    poweredBy: 'Desarrollado por',
    researchPrinciples: '167 Criterios de Auditoría respaldados por investigación',
    getExpertAnalysis: 'Obtener Análisis UX Experto',
    unlockFullAnalysis: (count: number) => `Desbloquea el análisis completo con recomendaciones detalladas para los ${count} problemas`,
    oneTimePaymentShort: 'pago único',
    completeAnalysisShort: 'Análisis completo de todos los problemas detectados',
    continueToCheckout: 'Continuar al Pago',
    processing: 'Procesando...',
    moneyBackGuarantee: GUARANTEE.es.checkout,
    emailAddress: 'Correo electrónico',
    // B-05 checkout inputs
    successActionLabel: '¿Qué es una visita exitosa a esta página?',
    successActionChoose: 'Elige una',
    successActionPurchase: 'Compra algo',
    successActionContact: 'Nos contacta, pide presupuesto o reserva',
    successActionSignup: 'Se registra o inicia una prueba',
    successActionInform: 'Encuentra información o completa una tarea',
    valueLabelPurchase: '¿Cuánto vale un pedido nuevo? (USD)',
    valueLabelContact: '¿Cuánto vale un cliente nuevo? (USD)',
    valueLabelSignup: '¿Cuánto vale un registro nuevo? (USD)',
    valueHint: 'Tu número. El reporte dice qué vale uno más; no predice nada.',
    valueInvalid: 'Escribe un número, por ejemplo 5000.',
    valueConfirm: '{value} USD por cada uno es inusual. Confirmo que es correcto.',
    refinementToggle: 'Tengo datos de tráfico de esta página (opcional)',
    refinementHint: 'Se necesitan ambos para los escenarios extra. Déjalos en blanco y el reporte funciona igual.',
    sessionsLabel: 'Sesiones mensuales a esta página',
    rateLabel: 'Porcentaje de visitas que completan la acción hoy (%)',
    // New: Urgent issue count
    weFoundIssues: (count: number) => `Encontramos ${count} problemas en tu sitio`,
    issuesNeedAttention: 'Estos problemas pueden estar afectando tus conversiones y experiencia de usuario',
    // New: Quick Wins teaser
    quickWinsTitle: 'Victorias Rápidas Disponibles',
    quickWinsDescription: 'Correcciones fáciles que toman menos de 1 hora',
    quickWinsLocked: 'Desbloquea con Informe Pro',
    quickWinsSample: 'Vista previa de correcciones accionables:',
    // New: Action Plan teaser
    actionPlanTitle: 'Plan de Acción Prioritario',
    actionPlanDescription: 'Los 5 problemas principales a corregir primero',
    actionPlanLocked: 'Incluido en Informe Pro',
  },
};

interface AccessibilityIssues {
  critical: number;
  serious: number;
  moderate: number;
  minor: number;
  total: number;
}

interface Finding {
  category: string;
  title?: string;
  principleTitle?: string;
  description: string;
  severity: string;
  recommendation?: string;
  auditCode?: string;
  auditCategoryLabel?: string;
}

interface FreeReportResultsProps {
  url: string;
  accessibilityIssues: AccessibilityIssues;
  timestamp: string;
  reportId: string;
  allFindings?: Finding[]; // All findings for blur preview
  totalFindings?: number;
  locale?: string;
  screenshotUrl?: string;
  /**
   * Whether this report has already been unlocked, decided on the server from
   * the report's stored owner email. localStorage alone is per-browser, so a
   * visitor who gave their email on a laptop hit the gate again on their phone,
   * and the nurture sequence emails a link to this page to people who have
   * already converted. Those are the two ways a client-only unlock punishes the
   * people who did what was asked.
   */
  unlocked?: boolean;
  /**
   * Lines for every check that produced no verdict (analysis_meta.notAssessed),
   * already in the page locale. A blocked capture is not a clean site: these
   * render instead of "0 issues".
   */
  notAssessedLines?: string[];
  /** True when the accessibility run itself produced no verdict. */
  accessibilityNotAssessed?: boolean;
}

export function FreeReportResults({
  url,
  accessibilityIssues,
  timestamp,
  reportId,
  allFindings = [],
  totalFindings = 0,
  locale = 'en',
  screenshotUrl,
  unlocked = false,
  notAssessedLines = [],
  accessibilityNotAssessed = false,
}: FreeReportResultsProps) {
  const router = useRouter();
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [showSoftGate, setShowSoftGate] = useState(false);
  const [emailUnlocked, setEmailUnlocked] = useState(unlocked);
  const [email, setEmail] = useState('');
  // B-05: what a successful visit ends with, and what one is worth. Captured
  // here, at checkout, and persisted on the report before the Polar hand-off.
  const [successAction, setSuccessAction] = useState<'purchase' | 'contact' | 'signup' | 'inform' | ''>('');
  const [conversionValue, setConversionValue] = useState('');
  const [valueConfirmed, setValueConfirmed] = useState(false);
  const [showRefinement, setShowRefinement] = useState(false);
  const [monthlySessions, setMonthlySessions] = useState('');
  const [actionRate, setActionRate] = useState('');
  const parsedValue = parseConversionValue(conversionValue);
  const needsValue = successAction !== '' && successAction !== 'inform';
  const valueNeedsConfirm = needsValue && parsedValue.ok && parsedValue.confirm && !valueConfirmed;
  const inputsReady = successAction !== '' && (!needsValue || (parsedValue.ok && !valueNeedsConfirm));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get translations
  const t = i18n[locale as keyof typeof i18n] || i18n.en;

  // Locale-aware number formatter
  const formatNumber = (n: number) =>
    new Intl.NumberFormat(locale === 'es' ? 'es-ES' : 'en-US').format(n);

  // Supplementary unlock path. The server decides via the `unlocked` prop; this
  // only covers the moment between submitting the email and the page being
  // re-rendered by the server. It can only ever turn the gate off, never on.
  useEffect(() => {
    const storedUnlock = localStorage.getItem(`audit_unlocked_${reportId}`);
    if (storedUnlock) {
      try {
        if (JSON.parse(storedUnlock)?.unlocked) setEmailUnlocked(true);
      } catch {
        // Invalid stored data, ignore
      }
    }
  }, [reportId]);

  // Handle successful soft gate submission
  const handleSoftGateSuccess = (submittedEmail: string) => {
    setEmailUnlocked(true);
    setShowSoftGate(false);
    // Persist unlock state
    localStorage.setItem(`audit_unlocked_${reportId}`, JSON.stringify({ email: submittedEmail, unlocked: true }));
    // The gate is enforced on the server (S-016): findings past the preview
    // count arrive without their description. /api/newsletter has already
    // stored the email on the report by the time onSuccess fires, so a refresh
    // re-renders this page with the unlocked rows filled in.
    router.refresh();
  };

  // Calculate findings for each tier
  const visibleCount = emailUnlocked
    ? unlockedCount(allFindings.length)
    : Math.min(PREVIEW_FINDINGS_COUNT, allFindings.length);
  const freeFindings = allFindings.slice(0, Math.min(PREVIEW_FINDINGS_COUNT, allFindings.length));
  const softGateFindings = allFindings.slice(freeFindings.length, visibleCount);
  const hiddenBySoftGate = Math.max(0, unlockedCount(allFindings.length) - freeFindings.length);

  // Focus trap refs for modal accessibility
  const modalRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const firstFocusableRef = useRef<HTMLButtonElement>(null);
  const lastFocusableRef = useRef<HTMLButtonElement>(null);

  const handleUpgrade = () => {
    setShowCheckoutModal(true);
    setError(null);
  };

  // Focus trap implementation for modal
  const handleModalKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      setShowCheckoutModal(false);
      triggerRef.current?.focus();
      return;
    }

    if (e.key !== 'Tab') return;

    const focusableElements = modalRef.current?.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (!focusableElements || focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (e.shiftKey && document.activeElement === firstElement) {
      e.preventDefault();
      lastElement.focus();
    } else if (!e.shiftKey && document.activeElement === lastElement) {
      e.preventDefault();
      firstElement.focus();
    }
  }, []);

  // Modal focus management
  useEffect(() => {
    if (showCheckoutModal) {
      // Focus first focusable element when modal opens
      const timer = setTimeout(() => {
        firstFocusableRef.current?.focus();
      }, 50);

      // Add keyboard listener for focus trap
      document.addEventListener('keydown', handleModalKeyDown);

      // Prevent body scroll
      document.body.style.overflow = 'hidden';

      return () => {
        clearTimeout(timer);
        document.removeEventListener('keydown', handleModalKeyDown);
        document.body.style.overflow = '';
      };
    } else {
      document.body.style.overflow = '';
    }
  }, [showCheckoutModal, handleModalKeyDown]);

  const handleCheckout = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/audit/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reportId,
          email,
          locale,
          successAction,
          conversionValue,
          monthlySessions: showRefinement ? monthlySessions : '',
          actionRate: showRefinement ? actionRate : '',
        }),
      });

      const data = await response.json();

      if (!data.success) {
        setError(data.error || 'Failed to create checkout');
        return;
      }

      // Redirect to Polar checkout
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  const backText = locale === 'es' ? 'Volver al panel' : 'Back to Dashboard';

  return (
    <div className={styles.container}>
      {/* Back Navigation */}
      <Link href={`/${locale}/dashboard`} className={styles.backLink}>
        <ArrowLeft size={16} />
        {backText}
      </Link>

      {/* Header */}
      <div className={styles.header}>
        <h1 className={styles.title}>{t.title}</h1>
        <p className={styles.url}>{url}</p>
        <p className={styles.timestamp}>
          {t.analyzedOn} {new Date(timestamp).toLocaleDateString(locale === 'es' ? 'es-ES' : 'en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </p>
        {screenshotUrl && (
          <a
            href="#screenshot-section"
            className={styles.headerThumbnail}
            title="View analyzed page screenshot"
          >
            <img
              src={screenshotUrl}
              alt={`Screenshot thumbnail of ${url}`}
              className={styles.headerThumbnailImg}
            />
          </a>
        )}
      </div>

      {/* Prominent Issue Count Hero */}
      <div className={styles.issueCountHero}>
        <div className={styles.issueCountNumber}>
          <span className={styles.issueCountValue}>{formatNumber(totalFindings || allFindings.length)}</span>
          <span className={styles.issueCountLabel}>{locale === 'es' ? 'problemas encontrados' : 'issues found'}</span>
        </div>
        <p className={styles.issueCountSubtext}>{t.issuesNeedAttention}</p>
        <div className={styles.issueCountBreakdown}>
          {accessibilityIssues.critical > 0 && (
            <span className={`${styles.issueCountBadge} ${styles.critical}`}>
              <AlertCircle size={14} />
              {formatNumber(accessibilityIssues.critical)} {t.critical}
            </span>
          )}
          {accessibilityIssues.serious > 0 && (
            <span className={`${styles.issueCountBadge} ${styles.serious}`}>
              <AlertTriangle size={14} />
              {formatNumber(accessibilityIssues.serious)} {t.serious}
            </span>
          )}
          {accessibilityIssues.moderate > 0 && (
            <span className={`${styles.issueCountBadge} ${styles.moderate}`}>
              <Info size={14} />
              {formatNumber(accessibilityIssues.moderate)} {t.moderate}
            </span>
          )}
        </div>
      </div>

      {/* Screenshot Section */}
      {screenshotUrl && (
        <section id="screenshot-section" className={styles.screenshotSection}>
          <h2 className={styles.sectionTitle}>{locale === 'es' ? 'Página Analizada' : 'Analyzed Page'}</h2>
          <div className={styles.screenshotWrapper}>
            <img src={screenshotUrl} alt={`Screenshot of ${url}`} className={styles.screenshot} />
            <div className={styles.screenshotOverlay} />
          </div>
        </section>
      )}

      {/* Accessibility Summary */}
      <div className={styles.summarySection}>
        <h2 className={styles.sectionTitle}>{t.accessibilityOverview}</h2>
        <p className={styles.sectionSubtitle}>
          {t.wcagCompliance}
        </p>

        {accessibilityNotAssessed ? (
          <p className={styles.sectionSubtitle}>{t.accessibilityNotAssessed}</p>
        ) : (
          <div className={styles.scoreGrid}>
            <div className={`${styles.scoreCard} ${styles.critical}`}>
              <div className={styles.scoreNumber}>{formatNumber(accessibilityIssues.critical)}</div>
              <div className={styles.scoreLabel}>{t.critical}</div>
            </div>
            <div className={`${styles.scoreCard} ${styles.serious}`}>
              <div className={styles.scoreNumber}>{formatNumber(accessibilityIssues.serious)}</div>
              <div className={styles.scoreLabel}>{t.serious}</div>
            </div>
            <div className={`${styles.scoreCard} ${styles.moderate}`}>
              <div className={styles.scoreNumber}>{formatNumber(accessibilityIssues.moderate)}</div>
              <div className={styles.scoreLabel}>{t.moderate}</div>
            </div>
            <div className={`${styles.scoreCard} ${styles.minor}`}>
              <div className={styles.scoreNumber}>{formatNumber(accessibilityIssues.minor)}</div>
              <div className={styles.scoreLabel}>{t.minor}</div>
            </div>
          </div>
        )}
      </div>

      {/* Checks that produced no verdict: unknown, never "0 issues" */}
      {notAssessedLines.length > 0 && (
        <div className={styles.issuesSection}>
          <div className={styles.issuesHeader}>
            <div>
              <h2 className={styles.sectionTitle}>{t.notAssessedTitle}</h2>
              <p className={styles.sectionSubtitle}>{t.notAssessedIntro}</p>
            </div>
          </div>
          <div className={styles.issuesList}>
            {notAssessedLines.map((line) => (
              <div key={line} className={styles.issueCard}>
                <p className={styles.issueDescription}>{line}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All Issues with Tiered Access */}
      {allFindings.length > 0 && (
        <div className={styles.issuesSection}>
          <div className={styles.issuesHeader}>
            <div>
              <h2 className={styles.sectionTitle}>{t.allIssuesDetected}</h2>
              <p className={styles.sectionSubtitle}>
                {t.showingIssues(visibleCount, totalFindings || allFindings.length)}
              </p>
            </div>
            <div className={styles.issuesStats}>
              <span className={styles.statBadge}>{t.totalFindings(totalFindings || allFindings.length)}</span>
            </div>
          </div>

          <div className={styles.issuesList}>
            {/* Preview: enough to prove the analysis is real and specific */}
            {freeFindings.map((finding, index) => (
              <div key={index} className={styles.issueCard}>
                <div className={styles.issueHeader}>
                  <SeverityMark severity={finding.severity} />
                  <span className={styles.issueCategory}>{finding.auditCategoryLabel || finding.category}</span>
                  <CriterionTag code={finding.auditCode} categoryLabel={finding.auditCategoryLabel} />
                </div>
                <h3 className={styles.issueTitle}>
                  {finding.title || finding.principleTitle || t.issueDetected}
                </h3>
                <p className={styles.issueDescription}>{finding.description}</p>
                {/* Risk context based on severity */}
                <p className={styles.issueImpact}>
                  {severityImpact[finding.severity]?.[locale as 'en' | 'es'] || severityImpact[finding.severity]?.en}
                </p>
              </div>
            ))}

            {/* Tier 2: Soft Gate - Email unlock for 5 more findings */}
            {hiddenBySoftGate > 0 && !emailUnlocked && (
              <div className={styles.softGateSection}>
                <div className={styles.softGateCard}>
                  <div className={styles.softGateIcon}>
                    <Gift size={28} />
                  </div>
                  <div className={styles.softGateContent}>
                    <h3 className={styles.softGateTitle}>
                      {t.unlockMoreFree(hiddenBySoftGate)}
                    </h3>
                    <p className={styles.softGateDescription}>
                      {t.unlockDescription}
                    </p>
                  </div>
                  <Button
                    size="lg"
                    onClick={() => setShowSoftGate(true)}
                    className={styles.softGateButton}
                  >
                    <Mail size={18} />
                    {t.unlockWithEmail}
                  </Button>
                </div>
              </div>
            )}

            {/* Tier 2 Findings - Shown after email unlock */}
            {emailUnlocked && softGateFindings.map((finding, index) => (
              <div key={index + freeFindings.length} className={styles.issueCard}>
                <div className={styles.issueHeader}>
                  <SeverityMark severity={finding.severity} />
                  <span className={styles.issueCategory}>{finding.auditCategoryLabel || finding.category}</span>
                  <CriterionTag code={finding.auditCode} categoryLabel={finding.auditCategoryLabel} />
                </div>
                <h3 className={styles.issueTitle}>
                  {finding.title || finding.principleTitle || t.issueDetected}
                </h3>
                <p className={styles.issueDescription}>{finding.description}</p>
                {/* Risk context based on severity */}
                <p className={styles.issueImpact}>
                  {severityImpact[finding.severity]?.[locale as 'en' | 'es'] || severityImpact[finding.severity]?.en}
                </p>
              </div>
            ))}

          </div>
        </div>
      )}

      {/* Quick Wins Teaser */}
      <div className={styles.quickWinsTeaser}>
        <div className={styles.quickWinsTeaserHeader}>
          <div className={styles.quickWinsTeaserIcon}>
            <Zap size={24} />
          </div>
          <div>
            <h3 className={styles.quickWinsTeaserTitle}>{t.quickWinsTitle}</h3>
            <p className={styles.quickWinsTeaserDescription}>{t.quickWinsDescription}</p>
          </div>
          <span className={styles.quickWinsLockedBadge}>
            <Lock size={12} />
            {t.quickWinsLocked}
          </span>
        </div>
        <div className={styles.quickWinsTeaserList}>
          {/* Show 2-3 blurred quick win previews from actual findings */}
          {allFindings.slice(0, 3).map((finding, index) => (
            <div key={index} className={styles.quickWinsTeaserItem}>
              <CheckCircle size={16} className={styles.quickWinsTeaserCheck} />
              <span className={styles.quickWinsTeaserText}>
                {finding.title || finding.principleTitle || t.issueDetected}
              </span>
              <span className={styles.quickWinsTeaserBlur} />
            </div>
          ))}
        </div>
      </div>

      {/* Action Plan Teaser */}
      <div className={styles.actionPlanTeaser}>
        <div className={styles.actionPlanTeaserHeader}>
          <div className={styles.actionPlanTeaserIcon}>
            <ListChecks size={24} />
          </div>
          <div>
            <h3 className={styles.actionPlanTeaserTitle}>{t.actionPlanTitle}</h3>
            <p className={styles.actionPlanTeaserDescription}>{t.actionPlanDescription}</p>
          </div>
          <span className={styles.actionPlanLockedBadge}>
            <Lock size={12} />
            {t.actionPlanLocked}
          </span>
        </div>
        <div className={styles.actionPlanTeaserSteps}>
          {[1, 2, 3, 4, 5].map((step) => (
            <div key={step} className={styles.actionPlanTeaserStep}>
              <span className={styles.actionPlanTeaserStepNumber}>{step}</span>
              <span className={styles.actionPlanTeaserStepBar} />
            </div>
          ))}
        </div>
      </div>

      {/* Upgrade CTA */}
      <div className={styles.upgradeSection}>
        <div className={styles.upgradeCard}>
          <div className={styles.upgradeHeader}>
            <h2 className={styles.upgradeTitle}>
              <Unlock size={24} className={styles.unlockIcon} aria-hidden="true" />
              {t.unlockExpertAnalysis}
            </h2>
            <div className={styles.upgradePrice}>
              <span className={styles.priceCurrency}>$</span>
              <span className={styles.priceAmount}>99</span>
            </div>
          </div>

          <p className={styles.upgradeSubtitle}>
            {t.professionalAnalysis}
          </p>

          <div className={styles.upgradeFeatures}>
            <div className={styles.featureItem}>
              <CheckCircle size={20} className={styles.featureIcon} />
              <span>{t.completeAnalysis(totalFindings || 'detected')}</span>
            </div>
            <div className={styles.featureItem}>
              <CheckCircle size={20} className={styles.featureIcon} />
              <span>{t.evaluatedAgainst}</span>
            </div>
            <div className={styles.featureItem}>
              <CheckCircle size={20} className={styles.featureIcon} />
              <span>{t.visualAttention}</span>
            </div>
            <div className={styles.featureItem}>
              <CheckCircle size={20} className={styles.featureIcon} />
              <span>{t.detailedRecommendations}</span>
            </div>
            <div className={styles.featureItem}>
              <CheckCircle size={20} className={styles.featureIcon} />
              <span>{t.pdfReport}</span>
            </div>
          </div>

          <Button
            ref={triggerRef}
            size="lg"
            onClick={handleUpgrade}
            className={styles.upgradeButton}
          >
            {t.getFullReport}
          </Button>

          <p className={styles.upgradeNote}>
            {t.oneTimePayment}
          </p>
        </div>
      </div>

      {/* Teaser Section */}
      <div className={styles.teaserSection}>
        <h3 className={styles.teaserTitle}>
          {t.wantMoreIssues}
        </h3>
        <div className={styles.teaserGrid}>
          <div className={styles.teaserCard}>
            <div className={styles.teaserIcon}>
              <Target size={24} aria-hidden="true" />
            </div>
            <h4>{t.visualHierarchy}</h4>
            <p className={styles.locked}>
              <Lock size={14} aria-hidden="true" /> {t.lockedInFree}
            </p>
          </div>
          <div className={styles.teaserCard}>
            <div className={styles.teaserIcon}>
              <Zap size={24} aria-hidden="true" />
            </div>
            <h4>{t.performanceBottlenecks}</h4>
            <p className={styles.locked}>
              <Lock size={14} aria-hidden="true" /> {t.lockedInFree}
            </p>
          </div>
          <div className={styles.teaserCard}>
            <div className={styles.teaserIcon}>
              <Compass size={24} aria-hidden="true" />
            </div>
            <h4>{t.navigationProblems}</h4>
            <p className={styles.locked}>
              <Lock size={14} aria-hidden="true" /> {t.lockedInFree}
            </p>
          </div>
          <div className={styles.teaserCard}>
            <div className={styles.teaserIcon}>
              <Smartphone size={24} aria-hidden="true" />
            </div>
            <h4>{t.mobileUxIssues}</h4>
            <p className={styles.locked}>
              <Lock size={14} aria-hidden="true" /> {t.lockedInFree}
            </p>
          </div>
        </div>
      </div>

      {/* Attribution Footer */}
      <div className={styles.attribution}>
        <p>
          {t.poweredBy} <strong>interfaceaudit.com</strong> • {t.researchPrinciples}
        </p>
      </div>

      {/* Checkout Modal with focus trap */}
      {showCheckoutModal && (
        <div
          className={styles.modalOverlay}
          onClick={() => {
            setShowCheckoutModal(false);
            triggerRef.current?.focus();
          }}
          role="presentation"
        >
          <div
            ref={modalRef}
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="checkout-modal-title"
            aria-describedby="checkout-modal-description"
          >
            <button
              ref={firstFocusableRef}
              className={styles.modalClose}
              onClick={() => {
                setShowCheckoutModal(false);
                triggerRef.current?.focus();
              }}
              aria-label="Close checkout dialog"
            >
              <X size={24} />
            </button>

            <div className={styles.modalHeader}>
              <h2 id="checkout-modal-title" className={styles.modalTitle}>{t.getExpertAnalysis}</h2>
              <p id="checkout-modal-description" className={styles.modalSubtitle}>
                {t.unlockFullAnalysis(totalFindings || allFindings.length)}
              </p>
            </div>

            <div className={styles.modalPrice}>
              <span className={styles.priceTag}>$99</span>
              <span className={styles.priceNote}>{t.oneTimePaymentShort}</span>
            </div>

            <ul className={styles.modalFeatures}>
              <li>
                <CheckCircle size={16} className={styles.featureCheck} aria-hidden="true" />
                {t.completeAnalysisShort}
              </li>
              <li>
                <CheckCircle size={16} className={styles.featureCheck} aria-hidden="true" />
                {t.evaluatedAgainst}
              </li>
              <li>
                <CheckCircle size={16} className={styles.featureCheck} aria-hidden="true" />
                {t.detailedRecommendations}
              </li>
              <li>
                <CheckCircle size={16} className={styles.featureCheck} aria-hidden="true" />
                {t.pdfReport}
              </li>
            </ul>

            <form onSubmit={handleCheckout} className={styles.checkoutForm}>
              {/* B-05: one question and one number. The report's headline is a
                  break-even built from these, so they are required. */}
              <div className={styles.field}>
                <label htmlFor="checkout-success-action" className={styles.inputLabel}>
                  {t.successActionLabel}
                </label>
                <select
                  id="checkout-success-action"
                  value={successAction}
                  onChange={(e) => { setSuccessAction(e.target.value as typeof successAction); setValueConfirmed(false); }}
                  required
                  className={styles.selectInput}
                  disabled={isLoading}
                >
                  <option value="" disabled>{t.successActionChoose}</option>
                  <option value="purchase">{t.successActionPurchase}</option>
                  <option value="contact">{t.successActionContact}</option>
                  <option value="signup">{t.successActionSignup}</option>
                  <option value="inform">{t.successActionInform}</option>
                </select>
              </div>

              {needsValue && (
                <div className={styles.field}>
                  <label htmlFor="checkout-conversion-value" className={styles.inputLabel}>
                    {successAction === 'purchase'
                      ? t.valueLabelPurchase
                      : successAction === 'signup'
                        ? t.valueLabelSignup
                        : t.valueLabelContact}
                  </label>
                  <input
                    id="checkout-conversion-value"
                    type="text"
                    inputMode="decimal"
                    value={conversionValue}
                    onChange={(e) => { setConversionValue(e.target.value); setValueConfirmed(false); }}
                    placeholder="5000"
                    required
                    className={styles.emailInput}
                    disabled={isLoading}
                    aria-describedby="checkout-conversion-value-hint"
                    aria-invalid={Boolean(conversionValue) && !parsedValue.ok}
                  />
                  <p
                    id="checkout-conversion-value-hint"
                    className={conversionValue && !parsedValue.ok ? styles.fieldError : styles.fieldHint}
                  >
                    {conversionValue && !parsedValue.ok ? t.valueInvalid : t.valueHint}
                  </p>
                  {valueNeedsConfirm && (
                    <label className={styles.confirmRow}>
                      <input
                        type="checkbox"
                        checked={valueConfirmed}
                        onChange={(e) => setValueConfirmed(e.target.checked)}
                        className={styles.confirmBox}
                      />
                      <span>{t.valueConfirm.replace('{value}', conversionValue)}</span>
                    </label>
                  )}
                </div>
              )}

              {needsValue && (
                <div className={styles.refinement}>
                  <label className={styles.confirmRow}>
                    <input
                      type="checkbox"
                      checked={showRefinement}
                      onChange={(e) => setShowRefinement(e.target.checked)}
                      className={styles.confirmBox}
                    />
                    <span>{t.refinementToggle}</span>
                  </label>
                  {showRefinement && (
                    <div className={styles.refinementGrid}>
                      <div className={styles.field}>
                        <label htmlFor="checkout-sessions" className={styles.inputLabel}>{t.sessionsLabel}</label>
                        <input
                          id="checkout-sessions"
                          type="text"
                          inputMode="numeric"
                          value={monthlySessions}
                          onChange={(e) => setMonthlySessions(e.target.value)}
                          placeholder="40000"
                          className={styles.emailInput}
                          disabled={isLoading}
                        />
                      </div>
                      <div className={styles.field}>
                        <label htmlFor="checkout-rate" className={styles.inputLabel}>{t.rateLabel}</label>
                        <input
                          id="checkout-rate"
                          type="text"
                          inputMode="decimal"
                          value={actionRate}
                          onChange={(e) => setActionRate(e.target.value)}
                          placeholder="2.1"
                          className={styles.emailInput}
                          disabled={isLoading}
                        />
                      </div>
                      <p className={styles.fieldHint}>{t.refinementHint}</p>
                    </div>
                  )}
                </div>
              )}

              <label htmlFor="checkout-email" className={styles.inputLabel}>
                {t.emailAddress}
              </label>
              <input
                id="checkout-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoComplete="email"
                className={styles.emailInput}
                disabled={isLoading}
              />

              {error && <p className={styles.errorMessage} role="alert">{error}</p>}

              <Button
                ref={lastFocusableRef}
                type="submit"
                size="lg"
                disabled={isLoading || !email || !inputsReady}
                className={styles.checkoutButton}
              >
                {isLoading ? (
                  <>
                    <Loader2 size={18} className={styles.spinner} aria-hidden="true" />
                    {t.processing}
                  </>
                ) : (
                  t.continueToCheckout
                )}
              </Button>

              <p className={styles.guarantee}>
                {t.moneyBackGuarantee}
              </p>
            </form>
          </div>
        </div>
      )}

      {/* Soft Gate Modal */}
      <SoftGateModal
        isOpen={showSoftGate}
        onClose={() => setShowSoftGate(false)}
        onSuccess={handleSoftGateSuccess}
        hiddenFindingsCount={hiddenBySoftGate}
        totalFindings={totalFindings || allFindings.length}
        reportId={reportId}
        source="soft_gate_audit"
      />
    </div>
  );
}
